import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import api from "@/lib/api";

const SEED_PROMPTS = [
  "What is HOFA?",
  "How do I volunteer?",
  "What is the ERSEH framework?",
  "Tell me about ESAFC",
];

export default function FAQChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Welcome to HOFA. Ask me anything about our programs, team, or how to get involved." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  async function sendMessage(text) {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/ai/faq", {
        question: q,
        history: next.slice(-7),
      });
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I'm having trouble right now — please try again or contact HOFA directly." }]);
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close assistant" : "Open HOFA assistant"}
        data-testid="faq-chatbot-toggle"
        className="fixed bottom-5 right-5 z-40 bg-palm text-white rounded-full h-14 w-14 flex items-center justify-center shadow-2xl"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
            className="fixed bottom-24 right-5 z-40 w-[min(380px,calc(100vw-2.5rem))] h-[520px] max-h-[70vh] bg-white rounded-[18px] shadow-2xl border border-ink/10 flex flex-col overflow-hidden"
            data-testid="faq-chatbot-panel"
          >
            <div className="px-5 py-4 bg-ink text-paper flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-mango flex items-center justify-center">
                <Sparkles size={16} className="text-palm-deep" />
              </div>
              <div className="flex-1">
                <div className="font-display text-base leading-tight">HOFA assistant</div>
                <div className="text-xs text-paper/60">GPT-6 Sol · ERSEH-grounded</div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 bg-paper space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-[14px] text-[14.5px] leading-relaxed ${
                      m.role === "user"
                        ? "bg-palm text-white rounded-br-sm"
                        : "bg-white border border-ink/10 text-ink rounded-bl-sm"
                    }`}
                    data-testid={`faq-msg-${m.role}-${i}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-white border border-ink/10 rounded-[14px] rounded-bl-sm px-3.5 py-2.5 text-ink/60 text-sm flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-ink/40 animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-ink/40 animate-pulse" style={{ animationDelay: "0.15s" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-ink/40 animate-pulse" style={{ animationDelay: "0.3s" }} />
                  </div>
                </div>
              )}
            </div>

            {messages.length <= 1 && (
              <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                {SEED_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => sendMessage(p)}
                    data-testid={`faq-seed-${p.toLowerCase().replace(/\s/g, "-")}`}
                    className="text-xs bg-ink/5 hover:bg-palm hover:text-white px-2.5 py-1 rounded-full font-semibold transition"
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="p-3 border-t border-ink/10 bg-white flex gap-2"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about HOFA…"
                data-testid="faq-input"
                disabled={sending}
                className="flex-1 rounded-full border border-ink/15 px-4 py-2 text-sm focus:outline-none focus:border-palm disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="faq-send-btn"
                className="bg-palm text-white rounded-full h-10 w-10 flex items-center justify-center disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
